import React, { useState, useRef, MouseEvent } from "react";
import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";

interface CarouselImageProps {
  src: string;
  alt: string;
}

const CarouselImage: React.FC<CarouselImageProps> = ({ src, alt }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1); // Zoom level
  const containerRef = useRef<HTMLDivElement | null>(null); // Ref to scrollable container
  const [isDragging, setIsDragging] = useState(false); // Track drag state
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const theme = useTheme();
  const imageUrls = `${src}?timestamp=${new Date().getTime()}`;

  // Zoom In Handler
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3)); // Max zoom: 3

  // Zoom Out Handler
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 1)); // Min zoom: 1

  // Start Dragging
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setStartPosition({
      x: e.clientX + (containerRef.current?.scrollLeft || 0),
      y: e.clientY + (containerRef.current?.scrollTop || 0),
    });
  };

  // Dragging to Simulate Scrolling
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;

    const dx = startPosition.x - e.clientX;
    const dy = startPosition.y - e.clientY;

    containerRef.current.scrollLeft = dx;
    containerRef.current.scrollTop = dy;
  };

  // Stop Dragging
  const handleMouseUp = () => setIsDragging(false);

  return (
    <Box
      sx={{
        width: "100%", // Full width
        height: "100%", // Full height
        position: "relative",
        overflow: "hidden",
        border: 1,
        borderColor: '#f2f0ef',
        borderRadius: 2
      }}
    >
      {/* Scrollable Image Container */}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto", // Enable scrolling
          position: "relative",
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp} // Stop dragging when leaving container
      >
        {isLoading && (
          <CircularProgress
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
            size={20}
          />
        )}
        <img
          src={src}
          alt={alt}
          style={{
            width: `${100 * scale}%`,
            height: `${100 * scale}%`,
            objectFit: "contain",
            display: "block",
            transition: "transform 0.3s ease-out",
            // WebkitMaskImage: `url(${imgSrc})`
          }}
          onLoad={() => setIsLoading(false)}
        />
      </Box>

      {/* Zoom Controls */}
      <Box
        sx={{
          position: "absolute",
          top: 10,
          left: 10,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 35,
            height: 35,
            backgroundColor: "white",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <IconButton onClick={zoomIn} color="primary" size="large">
            <Add />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: 35,
            height: 35,
            backgroundColor: "white",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <IconButton onClick={zoomOut} color="primary" size="large">
            <Remove />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default CarouselImage;
